import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FUENTE, useTema } from './tema';
import LienzoDeFirma from './LienzoDeFirma';
import { rubricaDeArchivo } from './rubricaDeImagen';
import { LETRAS, rubricaDeTexto, nombreLimpio, TOPE_LETRAS } from './rubricaDeTexto';
import * as guardada from './rubricaGuardada';

/**
 * TomarFirma — las cuatro maneras de poner la rúbrica, en un solo sitio.
 *
 * Dibujar, escribir, subir una imagen, o reutilizar la de la vez anterior.
 * Antes solo existía dibujar, y eso decidía por la gente: con el ratón sale un
 * garabato que no se parece a la firma de nadie, y en un portátil sin pantalla
 * táctil es casi todo el mundo. Quien firma el acta de la reunión de cada mes
 * no debería pelearse con un lienzo doce veces al año.
 *
 * ── Lo que sale de aquí es siempre lo mismo ─────────────────────────────
 *
 * Un PNG en base64, sin la cabecera `data:`, con fondo transparente. Da igual
 * por dónde entró: quien lo recibe —el servidor, el estampador del PDF— no
 * distingue una firma escrita de una dibujada, y no tiene por qué. Si tuviera
 * que distinguirlas, cada manera nueva de firmar obligaría a tocar el camino
 * entero hasta el PDF.
 *
 * ── Por qué la guardada no viene puesta ─────────────────────────────────
 *
 * Porque firmar tiene que ser un acto. Si al abrir la pantalla la firma ya
 * estuviera en su sitio, el único gesto que quedaría sería pulsar un botón
 * verde, y entonces la firma no dice «he leído esto»: dice «he pulsado». Se
 * ofrece, se ve, y hay que traerla.
 */

const estilos = (C) => ({
  pestanas: {
    display: 'flex', gap: 4, padding: 3, borderRadius: 9,
    background: C.panelAlto, border: `1px solid ${C.borde}`, marginBottom: 12,
    flexWrap: 'wrap'
  },
  pestana: (viva) => ({
    flex: '1 1 auto', minWidth: 104, padding: '7px 12px', borderRadius: 7,
    border: '1px solid ' + (viva ? C.borde : 'transparent'),
    background: viva ? C.fondo : 'transparent',
    color: viva ? C.texto : C.suave,
    fontSize: 13, fontWeight: viva ? 600 : 500, fontFamily: FUENTE,
    cursor: 'pointer', textAlign: 'center'
  }),

  campo: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    borderRadius: 8, border: `1px solid ${C.borde}`, background: C.panel,
    color: C.texto, fontSize: 14.5, fontFamily: FUENTE, outline: 'none'
  },

  letras: { display: 'flex', gap: 7, marginTop: 9, flexWrap: 'wrap' },
  letra: (viva, pila) => ({
    padding: '6px 14px', borderRadius: 7, cursor: 'pointer',
    border: `1px solid ${viva ? C.acento : C.borde}`,
    background: viva ? C.panelAlto : 'transparent',
    color: C.texto, fontSize: 17, fontFamily: pila, lineHeight: 1.4
  }),

  // El PNG ya generado, sobre un fondo claro fijo: la firma es negra y
  // transparente, y en modo oscuro sobre el panel no se vería nada.
  vista: {
    marginTop: 11, borderRadius: 10, border: `1px solid ${C.borde}`,
    background: '#ffffff', padding: 8, display: 'block'
  },
  imagen: { display: 'block', width: '100%', height: 'auto' },

  soltar: (encima) => ({
    border: `1px dashed ${encima ? C.acento : C.suave}`, borderRadius: 10,
    padding: '22px 16px', textAlign: 'center',
    background: encima ? C.panelAlto : C.panel,
    fontSize: 13.5, color: C.suave, lineHeight: 1.7, cursor: 'pointer'
  }),

  ficha: {
    display: 'flex', gap: 12, alignItems: 'center', padding: 11,
    borderRadius: 10, border: `1px solid ${C.borde}`, background: C.panelAlto,
    marginBottom: 12, flexWrap: 'wrap'
  },
  miniatura: {
    width: 128, height: 40, background: '#ffffff', borderRadius: 6,
    border: `1px solid ${C.borde}`, objectFit: 'contain', padding: 2, flexShrink: 0
  },

  boton: {
    padding: '7px 13px', borderRadius: 7, fontSize: 13, fontFamily: FUENTE,
    fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.acento}`,
    background: C.acento, color: C.acentoTexto
  },
  botonSuave: {
    padding: '7px 13px', borderRadius: 7, fontSize: 13, fontFamily: FUENTE,
    cursor: 'pointer', border: `1px solid ${C.borde}`,
    background: 'transparent', color: C.suave
  },

  nota: { fontSize: 12, color: C.tenue, lineHeight: 1.6, marginTop: 8 },
  malo: {
    marginTop: 10, padding: '10px 12px', borderRadius: 8, fontSize: 13,
    lineHeight: 1.6, color: C.error, border: `1px solid ${C.error}`,
    background: C.errorFondo || 'rgba(220,60,60,.10)'
  },
  recordar: {
    display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 13,
    fontSize: 12.5, color: C.suave, lineHeight: 1.6, cursor: 'pointer'
  }
});

const MODOS = [
  ['dibujar', 'Dibujarla'],
  ['escribir', 'Escribirla'],
  ['subir', 'Subir imagen']
];

/**
 * @param {Object} props
 * @param {Function} props.onCambio - recibe el PNG en base64 pelado, o null.
 * @param {string} props.nombre - el de quien firma; propuesta al escribirla
 * @param {string} props.correo - de quién es la firma guardada en este aparato
 * @param {boolean} props.desactivado
 */
const TomarFirma = ({ onCambio, nombre = '', correo = '', desactivado = false }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [modo, setModo] = useState('dibujar');
  const [texto, setTexto] = useState(nombre || '');
  const [letra, setLetra] = useState(LETRAS[0].id);
  const [subida, setSubida] = useState(null);
  const [encima, setEncima] = useState(false);
  const [malo, setMalo] = useState(null);
  const [recordar, setRecordar] = useState(false);
  const [guardadaAqui, setGuardadaAqui] = useState(null);
  const [rubrica, setRubrica] = useState(null);
  const archivoRef = useRef(null);

  // El nombre llega después que el componente: la pantalla se pinta antes de
  // saber quién firma. Solo se adopta mientras no se haya escrito nada, para no
  // borrarle a nadie lo que acaba de teclear.
  useEffect(() => {
    setTexto((previo) => (previo ? previo : (nombre || '')));
  }, [nombre]);

  useEffect(() => { setGuardadaAqui(guardada.leer(correo)); }, [correo]);

  // ── Una sola salida ─────────────────────────────────────────────────
  //
  // Todo pasa por aquí: así «recordar» se escribe una vez, y no una por cada
  // manera de firmar —que es como se acaba guardando en tres sitios y
  // olvidándose en uno—.
  const poner = useCallback((base64) => {
    setRubrica(base64 || null);
    if (onCambio) onCambio(base64 || null);
    if (!base64) return;
    if (recordar && correo) guardada.guardar(correo, base64);
  }, [onCambio, recordar, correo]);

  // ── Escribir ────────────────────────────────────────────────────────
  const dibujarTexto = useCallback((valor, cual) => {
    try {
      poner(rubricaDeTexto({ texto: valor, letra: cual }));
      setMalo(null);
    } catch {
      setMalo('Este navegador no ha podido dibujar el nombre. '
        + 'Prueba a dibujar la firma a mano.');
      poner(null);
    }
  }, [poner]);

  // Al entrar en «Escribirla» se pinta ya con lo que haya: una pestaña que
  // aparece vacía y espera a que toques algo parece no funcionar.
  useEffect(() => {
    if (modo !== 'escribir') return;
    dibujarTexto(texto, letra);
    // `dibujarTexto` cambia de identidad al marcar «recordar», y volver a
    // generar el PNG por eso sería trabajo de más sin efecto visible alguno.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, texto, letra]);

  // ── Subir ───────────────────────────────────────────────────────────
  const tomarArchivo = useCallback(async (archivo) => {
    setMalo(null);
    try {
      const base64 = await rubricaDeArchivo(archivo);
      setSubida(base64);
      poner(base64);
    } catch (e) {
      setSubida(null);
      poner(null);
      setMalo(e.message || 'No se pudo usar esa imagen.');
    }
  }, [poner]);

  const cambiarModo = (cual) => {
    if (cual === modo || desactivado) return;
    setModo(cual);
    setMalo(null);
    // Al cambiar de pestaña se suelta la rúbrica anterior. Dejarla puesta haría
    // que el botón de firmar se viera listo con la pestaña a la vista en
    // blanco: parecería que se manda lo que se ve, y se mandaría otra cosa.
    if (cual === 'subir') poner(subida);
    else if (cual === 'dibujar') poner(null);
  };

  const marcarRecordar = (quiere) => {
    setRecordar(quiere);
    if (!correo) return;
    if (quiere && rubrica) {
      const ok = guardada.guardar(correo, rubrica);
      setGuardadaAqui(ok ? guardada.leer(correo) : null);
      if (!ok) {
        setMalo('No se pudo guardar la firma en este navegador (puede estar en '
          + 'modo privado). La firma de ahora se manda igual.');
      }
    }
    if (!quiere) { guardada.olvidar(correo); setGuardadaAqui(null); }
  };

  const usarGuardada = () => {
    setSubida(guardadaAqui.firma);
    setModo('subir');
    poner(guardadaAqui.firma);
  };

  return (
    <div>
      {/* ── La de la vez anterior ──────────────────────────────────── */}
      {guardadaAqui && (
        <div style={S.ficha}>
          <img
            src={`data:image/png;base64,${guardadaAqui.firma}`}
            alt="Tu firma guardada en este aparato"
            style={S.miniatura}
          />
          <div style={{ flex: '1 1 160px', fontSize: 13, color: C.suave, lineHeight: 1.6 }}>
            Tu firma, {guardada.cuandoSeGuardo(guardadaAqui.cuando)} en este navegador.
          </div>
          <button type="button" style={S.boton} disabled={desactivado} onClick={usarGuardada}>
            Usar esta
          </button>
          <button
            type="button"
            style={S.botonSuave}
            disabled={desactivado}
            onClick={() => {
              guardada.olvidar(correo);
              setGuardadaAqui(null);
              setRecordar(false);
            }}
          >
            Olvidarla
          </button>
        </div>
      )}

      <div style={S.pestanas} role="tablist" aria-label="Cómo quieres firmar">
        {MODOS.map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={modo === id}
            style={S.pestana(modo === id)}
            disabled={desactivado}
            onClick={() => cambiarModo(id)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {modo === 'dibujar' && (
        <LienzoDeFirma onCambio={poner} nombre={nombre} desactivado={desactivado} />
      )}

      {modo === 'escribir' && (
        <div>
          <input
            style={S.campo}
            value={texto}
            maxLength={TOPE_LETRAS}
            disabled={desactivado}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Tu nombre, tal y como quieres que aparezca"
            aria-label="Nombre para la firma"
          />
          <div style={S.letras}>
            {LETRAS.map((l) => (
              <button
                key={l.id}
                type="button"
                style={S.letra(letra === l.id, l.pila)}
                disabled={desactivado}
                onClick={() => setLetra(l.id)}
                title={l.nombre}
              >
                {nombreLimpio(texto).slice(0, 14) || l.nombre}
              </button>
            ))}
          </div>
          {rubrica && (
            <div style={S.vista}>
              <img
                src={`data:image/png;base64,${rubrica}`}
                alt="Tu firma escrita"
                style={S.imagen}
              />
            </div>
          )}
          <div style={S.nota}>
            Esto es exactamente lo que se estampará en tu fila del acta.
          </div>
        </div>
      )}

      {modo === 'subir' && (
        <div>
          <div
            style={S.soltar(encima)}
            onClick={() => !desactivado && archivoRef.current && archivoRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
            onDragLeave={() => setEncima(false)}
            onDrop={(e) => {
              e.preventDefault();
              setEncima(false);
              const f = e.dataTransfer.files && e.dataTransfer.files[0];
              if (f) tomarArchivo(f);
            }}
          >
            <strong style={{ color: C.texto }}>Arrastra aquí la imagen de tu firma</strong>
            <br />
            o pulsa para buscarla. PNG, JPG o WEBP.
            <br />
            <span style={{ fontSize: 12.5, color: C.tenue }}>
              {/* Lo que la gente sube es la foto de un papel. Decirlo de
                  antemano evita la pregunta de por qué su firma se ve sin
                  fondo y más pequeña de lo que subió. */}
              Se recorta a la firma y se le quita el fondo blanco.
            </span>
          </div>
          <input
            ref={archivoRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              // Se vacía el input: si no, volver a elegir el MISMO archivo
              // —después de un error— no dispara ningún cambio, y parece que la
              // pantalla se ha quedado colgada.
              e.target.value = '';
              if (f) tomarArchivo(f);
            }}
          />
          {subida && (
            <div style={S.vista}>
              <img
                src={`data:image/png;base64,${subida}`}
                alt="Tu firma subida"
                style={S.imagen}
              />
            </div>
          )}
        </div>
      )}

      {malo && <div style={S.malo}>{malo}</div>}

      {correo && (
        <label style={S.recordar}>
          <input
            type="checkbox"
            checked={recordar}
            disabled={desactivado}
            onChange={(e) => marcarRecordar(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            Recordar mi firma en este navegador para la próxima vez.{' '}
            {/* Dónde se guarda es relevante: alguien firma desde el ordenador
                de la oficina y tiene que poder decidir con eso delante. */}
            <span style={{ color: C.tenue }}>
              No sale de este aparato y se puede borrar desde aquí.
            </span>
          </span>
        </label>
      )}
    </div>
  );
};

export default TomarFirma;
